// src/pages/FatigueManagement.tsx — G-13: Fatigue Risk Management
// Roster-integrated: flags <10hr rest between shifts, >12hr single shift, 3+ consecutive night shifts.
// WHSO dashboard alerts. Per-worker rolling history.
// WHS Act 2011 (Qld) s.19 · Managing Fatigue at Work CoP (Safe Work Australia)

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_EMPLOYEES, getEmployeeFMStatus } from '../utils/mockDataExtended'
import { MOCK_ALLOCATIONS, DayAllocation } from '../utils/mockDataRoster'
import { Moon, AlertTriangle, CheckCircle, Download, Info } from 'lucide-react'

const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })

// ── Fatigue rule definitions ──────────────────────────────────────
const FATIGUE_RULES = [
  { id: 'F1', label: 'Minimum rest between consecutive shifts', threshold: '<10 hours between shift end and next shift start', risk: 'HIGH' },
  { id: 'F2', label: 'Maximum shift duration', threshold: '>12 hours in a single shift', risk: 'HIGH' },
  { id: 'F3', label: 'Maximum consecutive days', threshold: '>6 consecutive working days without a rest day', risk: 'HIGH' },
  { id: 'F4', label: 'Consecutive night shifts', threshold: '>3 consecutive night shifts before mandatory day off', risk: 'MEDIUM' },
  { id: 'F5', label: 'Night shift return to day', threshold: '<48 hours between last night shift and first day shift', risk: 'MEDIUM' },
]

// NOTE: getEmployeeFMStatus returns 'RED' | 'AMBER' | 'CLEAR' — matching the type here
type FMStatus = 'RED' | 'AMBER' | 'CLEAR'

interface EmployeeFatigueProfile {
  id: string
  name: string
  fmStatus: FMStatus
  consecutiveDays: number
  consecutiveNights: number
  hoursThisWeek: number
  lastRestDay: string | null
  allocations: DayAllocation[]
  flags: string[]
}

function buildFatigueProfiles(allAllocations: DayAllocation[]): EmployeeFatigueProfile[] {
  return MOCK_EMPLOYEES.map(emp => {
    const empAllocs = allAllocations
      .filter(a => a.employeeId === emp.id)
      .sort((a, b) => a.date.localeCompare(b.date))

    // Consecutive days from the most recent allocation backwards
    const uniqueDates = [...new Set(empAllocs.map(a => a.date))].sort()
    let consecutiveDays = 0
    let streak = 0
    let lastRestDay: string | null = null
    for (let i = uniqueDates.length - 1; i >= 0; i--) {
      const prev = i > 0 ? uniqueDates[i - 1] : null
      if (prev) {
        const gap = (new Date(uniqueDates[i]).getTime() - new Date(prev).getTime()) / 86400000
        if (gap === 1) {
          streak++
        } else {
          consecutiveDays = streak + 1
          lastRestDay = prev
          break
        }
      } else {
        consecutiveDays = streak + 1
      }
    }
    if (consecutiveDays === 0 && uniqueDates.length > 0) consecutiveDays = 1

    // Consecutive nights
    const nightDates = [...new Set(empAllocs.filter(a => a.shiftType === 'NIGHT').map(a => a.date))].sort()
    let consecutiveNights = 0
    if (nightDates.length > 0) {
      let ns = 1
      for (let i = nightDates.length - 1; i > 0; i--) {
        const gap = (new Date(nightDates[i]).getTime() - new Date(nightDates[i - 1]).getTime()) / 86400000
        if (gap <= 2) ns++ // Sun–Thu pattern allows up to 2-day gap
        else break
      }
      consecutiveNights = ns
    }

    const hoursThisWeek = empAllocs.filter(a => {
      const d = new Date(a.date)
      const now = new Date('2026-07-18')
      const weekStart = new Date(now)
      weekStart.setDate(now.getDate() - now.getDay())
      return d >= weekStart
    }).length * 9

    // getEmployeeFMStatus returns 'RED' | 'AMBER' | 'CLEAR'
    const fmStatus: FMStatus = getEmployeeFMStatus(emp.id, allAllocations)

    const flags: string[] = []
    if (consecutiveDays >= 6) flags.push(`F3: ${consecutiveDays} consecutive days`)
    if (consecutiveNights >= 3) flags.push(`F4: ${consecutiveNights} consecutive nights`)
    if (hoursThisWeek > 50) flags.push(`F3: ${hoursThisWeek}h this week`)

    return {
      id: emp.id,
      name: emp.fullName,
      fmStatus,
      consecutiveDays,
      consecutiveNights,
      hoursThisWeek,
      lastRestDay: lastRestDay ? fmtDate(lastRestDay) : null,
      allocations: empAllocs,
      flags,
    }
  })
}

export function FatigueManagement() {
  const { isTier } = useAuthStore()
  const canAcknowledge = isTier('B_PC') || isTier('B_PM') || isTier('A_DEVELOPER')

  // Use MOCK_ALLOCATIONS only — NIGHT_ALLOCATIONS_SEED is local to Roster.tsx
  const profiles = buildFatigueProfiles([...MOCK_ALLOCATIONS])

  const [filter, setFilter] = useState<'ALL' | FMStatus>('ALL')
  const [acknowledged, setAcknowledged] = useState<string[]>([])

  const red    = profiles.filter(p => p.fmStatus === 'RED')
  const amber  = profiles.filter(p => p.fmStatus === 'AMBER')
  const clear  = profiles.filter(p => p.fmStatus === 'CLEAR')
  const filtered = profiles.filter(p => filter === 'ALL' || p.fmStatus === filter)

  // FM_META keyed to 'RED' | 'AMBER' | 'CLEAR' matching getEmployeeFMStatus return type
  const FM_META: Record<FMStatus, { label: string; bg: string; text: string; border: string; dot: string }> = {
    RED:   { label: 'Mandatory rest required',  bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    dot: 'bg-red-500' },
    AMBER: { label: 'Advisory — schedule rest', bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  dot: 'bg-amber-500' },
    CLEAR: { label: 'No fatigue concerns',      bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200',  dot: 'bg-green-500' },
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Moon className="w-6 h-6 text-indigo-500"/>Fatigue Risk Management
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Roster-integrated · consecutive shift detection · WHS Act 2011 s.19 · Managing Fatigue at Work CoP</p>
        </div>
        <button className="btn-secondary text-sm flex items-center gap-2"><Download className="w-4 h-4"/>Export fatigue register</button>
      </div>

      {red.length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-800">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0"/>
          <span><strong>{red.length} crew member{red.length !== 1 ? 's' : ''} require a mandatory rest day</strong> before their next roster allocation. FM RED — do not allocate until rest period is confirmed.</span>
        </div>
      )}

      {/* Rule reference */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5"/>Fatigue management thresholds — MCB Civil Electrical policy (WHS Act 2011 s.19)
        </div>
        <div className="grid grid-cols-2 gap-2">
          {FATIGUE_RULES.map(r => (
            <div key={r.id} className="text-xs bg-white rounded-lg px-3 py-2 border border-blue-100">
              <span className={`font-bold mr-1 ${r.risk === 'HIGH' ? 'text-red-600' : 'text-amber-600'}`}>{r.id}</span>
              <span className="text-blue-900 font-medium">{r.label}:</span>
              <span className="text-blue-700 ml-1">{r.threshold}</span>
            </div>
          ))}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'FM RED — rest required',   value: red.length,   color: 'text-red-700',   bg: 'bg-red-50 border-red-200' },
          { label: 'FM AMBER — advisory',       value: amber.length, color: amber.length > 0 ? 'text-amber-700' : 'text-gray-400', bg: amber.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200' },
          { label: 'FM CLEAR — no concerns',    value: clear.length, color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
        ].map(m => (
          <div key={m.label} className={`border rounded-xl px-4 py-3 ${m.bg}`}>
            <div className={`text-2xl font-bold ${m.color}`}>{m.value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{m.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {(['ALL', 'RED', 'AMBER', 'CLEAR'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium ${filter === f ? 'bg-[#1F3864] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {f === 'ALL' ? `All crew (${profiles.length})` : f === 'RED' ? `FM RED (${red.length})` : f === 'AMBER' ? `FM AMBER (${amber.length})` : `FM CLEAR (${clear.length})`}
          </button>
        ))}
      </div>

      {/* Employee cards */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">
            No crew members match this filter
          </div>
        )}
        {filtered.map(p => {
          const fm = FM_META[p.fmStatus]
          const isAck = acknowledged.includes(p.id)
          return (
            <div key={p.id} className={`border rounded-xl p-4 ${
              p.fmStatus === 'RED' && !isAck ? 'border-red-300 bg-red-50/20' :
              p.fmStatus === 'AMBER' && !isAck ? 'border-amber-300' :
              'border-gray-200 bg-white'
            }`}>
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <div className="font-semibold text-gray-900">{p.name}</div>
                    <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border ${fm.bg} ${fm.text} ${fm.border}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${fm.dot}`}/>
                      FM {p.fmStatus} — {fm.label}
                    </span>
                    {isAck && (
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-200 font-medium">
                        Acknowledged by WHSO
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <div className={`rounded-lg px-3 py-2 text-center ${p.consecutiveDays >= 6 ? 'bg-red-50 border border-red-200' : p.consecutiveDays >= 4 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                      <div className={`text-xl font-bold ${p.consecutiveDays >= 6 ? 'text-red-700' : p.consecutiveDays >= 4 ? 'text-amber-700' : 'text-gray-700'}`}>{p.consecutiveDays}</div>
                      <div className="text-xs text-gray-500">Consecutive days</div>
                    </div>
                    <div className={`rounded-lg px-3 py-2 text-center ${p.consecutiveNights >= 3 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                      <div className={`text-xl font-bold ${p.consecutiveNights >= 3 ? 'text-amber-700' : 'text-gray-700'}`}>{p.consecutiveNights}</div>
                      <div className="text-xs text-gray-500">Night shifts in row</div>
                    </div>
                    <div className={`rounded-lg px-3 py-2 text-center ${p.hoursThisWeek > 50 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                      <div className={`text-xl font-bold ${p.hoursThisWeek > 50 ? 'text-amber-700' : 'text-gray-700'}`}>{p.hoursThisWeek}</div>
                      <div className="text-xs text-gray-500">Hrs this week</div>
                    </div>
                    <div className="rounded-lg px-3 py-2 text-center bg-gray-50">
                      <div className="text-sm font-medium text-gray-700">{p.lastRestDay ?? '—'}</div>
                      <div className="text-xs text-gray-500">Last rest day</div>
                    </div>
                  </div>
                  {p.flags.length > 0 && (
                    <div className="flex gap-2 flex-wrap mt-2">
                      {p.flags.map(f => (
                        <span key={f} className="text-[10px] bg-red-100 text-red-700 border border-red-200 px-2 py-0.5 rounded-full font-medium">{f}</span>
                      ))}
                    </div>
                  )}
                </div>
                {(p.fmStatus === 'RED' || p.fmStatus === 'AMBER') && canAcknowledge && !isAck && (
                  <div className="flex-shrink-0 flex flex-col gap-2">
                    <button onClick={() => setAcknowledged(a => [...a, p.id])}
                      className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-lg font-medium">
                      Acknowledge fatigue flag
                    </button>
                    {p.fmStatus === 'RED' && (
                      <button className="text-xs border border-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50">
                        Push alert to WHSO inbox
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-600">
        <strong>Roster publication gate:</strong> Any roster containing FM RED patterns requires WHSO or PM acknowledgement before it can be published to crew. This acknowledgement is logged in the roster change history. WHS Act 2011 (Qld) s.19 — the PCBU must manage all reasonably foreseeable risks, including fatigue.
      </div>
    </div>
  )
}
